import { Building2, Users, Warehouse, Package, Handshake, Tractor } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import MarqueeStrip from "@/components/MarqueeStrip";
import IndiaMap from "@/components/IndiaMap";
import Partners from "@/components/Partners";
import { FadeUp } from "@/components/Reveal";
import { IMAGES, TIMELINE, LEADERS, STATS, MAP_STATES, NETWORK } from "@/data/content";
import SEO from "@/components/SEO";

const NETWORK_ICONS = { Building2, Users, Warehouse, Package, Handshake, Tractor };

const About = () => (
  <>
    <SEO
      title="About Us"
      description="Seed Park India Pvt. Ltd. — a technology-driven hybrid seed company with DSIR-recognised research farms, presence across 14 states and collaborations with India's leading agri-institutions. An MS Group company."
    />
    <PageHero
      eyebrow="About Us"
      titleLines={["Bred by science.", "Trusted in the field."]}
      description="Seed Park India Pvt. Ltd. is a progressive, technology-driven hybrid seed company developing high-performance hybrids for higher yields, greater adaptability and consistent field performance."
      image={IMAGES.field}
    />

    <section data-testid="about-vision-section" className="py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="Vision & Mission"
            titleLines={["Genetics that move", "Indian agriculture forward."]}
          />
        </div>
        <div className="space-y-10 lg:col-span-6">
          <FadeUp>
            <div className="border-l-2 border-brand-400 pl-6">
              <h3 className="font-display text-2xl font-bold tracking-tight">Our Vision</h3>
              <p className="mt-3 text-base leading-relaxed text-mist">
                To be India's most trusted hybrid seed science company — where every product is a result of
                rigorous research and every farmer partnership is built on documented performance.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="border-l-2 border-gold-400 pl-6">
              <h3 className="font-display text-2xl font-bold tracking-tight">Our Mission</h3>
              <p className="mt-3 text-base leading-relaxed text-mist">
                To combine advanced genetics, intensive breeding and multi-location field testing into hybrids
                suited to diverse agro-climatic conditions — and to put farmer prosperity at the centre of
                every decision we make.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-4">
              {STATS.map((s) => (
                <div key={s.label} className="bg-forest-900 p-5">
                  <p className="font-display text-2xl font-black text-brand-400">{s.value}</p>
                  <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-sage">{s.label}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <MarqueeStrip dark />

    <section data-testid="about-presence-section" className="bg-corp-700 py-28 lg:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-6">
          <FadeUp>
            <p className="mb-4 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-gold-300">
              <span className="inline-block h-px w-8 bg-gold-300" /> Our Presence
            </p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Fourteen states. One promise to farmers.
            </h2>
          </FadeUp>
          <FadeUp delay={0.2}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/75">
              From the cotton belts of Telangana to the paddy deltas of West Bengal and the wheat fields of
              Uttarakhand — Seed Park research, production and dealer networks serve farmers across India.
            </p>
          </FadeUp>
          <FadeUp delay={0.25}>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="about-states-list">
              {MAP_STATES.map((s) => (
                <span key={s.name} className="border border-white/20 px-3 py-1.5 text-xs font-medium text-white/85">
                  {s.name}
                </span>
              ))}
            </div>
          </FadeUp>
        </div>
        <div className="lg:col-span-6">
          <FadeUp delay={0.15}>
            <IndiaMap states={MAP_STATES} testId="about-india-map" />
          </FadeUp>
        </div>
      </div>
    </section>

    <section data-testid="about-network-section" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Our Network"
          titleLines={["The infrastructure behind", "every Seed Park pack."]}
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {NETWORK.map((n, i) => {
            const Icon = NETWORK_ICONS[n.icon];
            return (
              <FadeUp key={n.label} delay={(i % 3) * 0.08} className="h-full">
                <div className="group h-full border border-[#123C6B]/10 bg-forest-900 p-8 transition-colors hover:border-brand-400/40" data-testid={`network-card-${i}`}>
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center bg-brand-400/10 text-brand-400">
                      <Icon className="h-6 w-6 animate-bob" style={{ animationDelay: `${i * 0.45}s` }} />
                    </span>
                    <p className="font-display text-3xl font-black tracking-tight text-corp-500">{n.value}</p>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold tracking-tight">{n.label}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{n.text}</p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>

    <Partners dark />

    <section data-testid="about-timeline-section" className="py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Our Journey"
          titleLines={["A company built", "one season at a time."]}
        />
        <div className="relative ml-3 border-l border-white/15 pl-10">
          {TIMELINE.map((t, i) => (
            <FadeUp key={t.year} delay={i * 0.05}>
              <div className="relative pb-14 last:pb-0" data-testid={`timeline-item-${i}`}>
                <span className="absolute -left-[46px] top-1.5 h-3 w-3 rounded-full border-2 border-brand-400 bg-forest-950" />
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-400">{t.year}</p>
                <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">{t.title}</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-mist">{t.text}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    <section data-testid="about-leadership-section" className="bg-forest-900 py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          eyebrow="Leadership"
          titleLines={["Breeders first.", "Executives second."]}
        />
        <div className="grid gap-5 md:grid-cols-3">
          {LEADERS.map((l, i) => (
            <FadeUp key={l.name} delay={i * 0.1} className="h-full">
              <div className="group h-full border border-white/10 bg-forest-950 p-8 transition-colors hover:border-brand-400/40" data-testid={`leader-card-${i}`}>
                <div className="flex h-14 w-14 items-center justify-center border border-brand-400/40 font-display text-xl font-black text-brand-400">
                  {l.name.split(" ").map((n) => n[0]).join("").replace(".", "")}
                </div>
                <h3 className="mt-6 font-display text-2xl font-bold tracking-tight">{l.name}</h3>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-400">{l.role}</p>
                <p className="mt-4 text-sm leading-relaxed text-sage">{l.bio}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default About;
